import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  adminApi,
  clearAdminAuth,
  getStoredAdminAuth,
  setAdminUnauthorizedHandler,
  storeAdminAuth,
} from '../services/api';
import { Routes } from '../routes';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const history = useHistory();
  const stored = getStoredAdminAuth();
  const [accessToken, setAccessToken] = useState(stored.accessToken);
  const [username, setUsername] = useState(stored.username);

  const isAdminAuthenticated = Boolean(accessToken);

  const logout = useCallback(() => {
    clearAdminAuth();
    setAccessToken('');
    setUsername('');
    history.push(Routes.AdminLogin.path);
  }, [history]);

  useEffect(() => {
    setAdminUnauthorizedHandler(logout);
    return () => setAdminUnauthorizedHandler(null);
  }, [logout]);

  const login = async (user, password) => {
    const { data } = await adminApi.login(user, password);
    const token = data.accessToken || data.access_token;
    if (!token) {
      throw new Error('Login succeeded but no accessToken was returned');
    }
    setAccessToken(token);
    setUsername(user);
    storeAdminAuth({ accessToken: token, username: user });
    return data;
  };

  const value = useMemo(
    () => ({
      accessToken,
      username,
      isAdminAuthenticated,
      login,
      logout,
    }),
    [accessToken, username, isAdminAuthenticated, logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
